import { SentryError } from '@ifixit/sentry';
import { useShallowMemo } from '@ifixit/utils-react/hooks';
import debounce from 'lodash/debounce';
import { useEffect } from 'react';
import type { AnalyticsItem, AnalyticsItemsEvent, CustomAnalyticsEvent, GrowthbookEvent } from '..';

type GA4GrowthbookEvent = {
   eventName: string;
} & GrowthbookEvent;

type GTagViewItemsListEvent = {
   items: AnalyticsItem[];
   item_list_id: string;
   item_list_name: string;
};

type GA4InitialState = {
   page_referrer: string;
   page_location: string;
   page_title: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GTagArg = Array<any>;

export type GTag = (...args: GTagArg) => void;
declare global {
   interface Window {
      gtag?: GTag;
      dataLayer?: GTagArg;
      __ga4_initial_state?: GA4InitialState;
   }
}

/**
 * All GA4 user properties we set on the session.
 *
 * All properties except `user_privilege` are available synchronously at page-frame render time
 * and are passed to `initGA4ArrayOnWindow` to ensure they are set before GrowthBook initializes
 * and any `experiment_viewed` events fire.
 *
 * `user_privilege` is omitted from init because it requires an async auth API response
 * (`useAuthenticatedUser`). It is set later via `useUpdateGA4UserProperties`.
 * TODO: set user_privilege synchronously if it becomes available server-side.
 */
export type GA4UserProperties = {
   preferred_store: string;
   preferred_store_locale: string;
   preferred_language: string;
   customer_type: 'pro' | 'regular';
   on_mobile_app: 'true' | 'false';
   consent_banner_shown: 'true' | 'false';
   logged_in: 'true' | 'false';
   user_privilege: string | null;
};

let DEBUG_MODE = false;
let ga4Initialized = false;

if (typeof window !== 'undefined') {
   DEBUG_MODE = window.location.search.includes('ga4_debug=true');

   window.dataLayer = window.dataLayer || [];
   window.gtag = windowGtag;
   window.gtag('js', new Date());
}

function gtag(...args: GTagArg) {
   if (typeof window === 'undefined') {
      return;
   }

   if (!window.gtag) {
      throw new SentryError('window.gtag is not defined');
   }

   if (DEBUG_MODE) {
      // eslint-disable-next-line no-console
      console.log('[GA4] gtag', ...args);
   }

   window.gtag(...args);
}

function windowGtag() {
   // eslint-disable-next-line prefer-rest-params
   window?.dataLayer?.push(arguments);
}

/**
 * Initializes GA4 by calling `gtag('config')`, which also sends the initial
 * `page_view` event. Idempotent — safe to call multiple times, only the first
 * call takes effect. Subsequent page_view events for SPA navigations must be
 * sent separately via `trackGA4PageView`.
 */
export function initGA4ArrayOnWindow({
   GtagID,
   debugMode,
   userProperties,
}: {
   GtagID: string | undefined;
   debugMode: boolean;
   userProperties: Omit<GA4UserProperties, 'user_privilege'>;
}) {
   if (typeof window === 'undefined' || !GtagID || !window.gtag) {
      return;
   }

   if (ga4Initialized) {
      return;
   }

   if (debugMode) {
      DEBUG_MODE = true;
   }

   // Set user_properties before gtag('config') so they are guaranteed to be present
   // on every subsequent event, including experiment_viewed fired by GrowthBook's
   // synchronous initSync() immediately after this call.
   gtag('set', 'user_properties', userProperties);

   const config: Record<string, unknown> = DEBUG_MODE ? { debug_mode: true } : {};

   // Use captured state if available (defensive against timing issues)
   // This preserves accurate attribution even if SPA navigation occurs before gtag('config')
   if (window.__ga4_initial_state) {
      config.page_referrer = window.__ga4_initial_state.page_referrer;
      config.page_location = window.__ga4_initial_state.page_location;
      config.page_title = window.__ga4_initial_state.page_title;
   }

   const mobileClientId = new URLSearchParams(window.location.search).get('_ga');
   if (mobileClientId) {
      config.client_id = mobileClientId;
   }

   gtag('config', GtagID, config);
   ga4Initialized = true;
}

/**
 * Updates GA4 user_properties whenever the provided values change.
 *
 * Call this in a component that has access to async data (e.g. auth) so that
 * user_privilege is updated once the auth API responds. The other properties
 * mirror what was set synchronously in initGA4ArrayOnWindow — re-setting them
 * here ensures they stay current if locale changes during the session.
 */
export function useUpdateGA4UserProperties(properties: GA4UserProperties) {
   const props = useShallowMemo(properties);

   useEffect(() => {
      gtag('set', 'user_properties', props);
   }, [props]);
}

export function setGA4Consent({ consent, type }: { consent: boolean; type: 'default' | 'update' }) {
   const consentValue = consent ? 'granted' : 'denied';
   gtag('consent', type, {
      ad_storage: consentValue,
      ad_user_data: consentValue,
      ad_personalization: consentValue,
      analytics_storage: consentValue,
   });
   gtag('set', 'user_properties', { consent_choice: consentValue });
   gtag('event', `consent_${type}`, { consentValue });
}

export function trackGA4ViewItem(event: AnalyticsItemsEvent) {
   gtag('event', 'view_item', formatEvent(event));
}

export function trackGA4ViewCart(event: AnalyticsItemsEvent) {
   gtag('event', 'view_cart', formatEvent(event));
}

export function trackGA4AddToCart(event: AnalyticsItemsEvent) {
   gtag('event', 'add_to_cart', formatEvent(event));
}

export function trackGA4RemoveFromCart(event: AnalyticsItemsEvent) {
   gtag('event', 'remove_from_cart', formatEvent(event));
}

export function trackGA4ViewItemList(event: GTagViewItemsListEvent) {
   gtag('event', 'view_item_list', event);
}

export const debouncedTrackGA4ViewItemList: GTag = debounce((event: GTagViewItemsListEvent) => {
   trackGA4ViewItemList(event);
}, 500);

function formatEvent(event: AnalyticsItemsEvent) {
   const { items, ...rest } = event;
   const formatedItems = items.map(item => {
      const { categories, ...rest } = item;
      return {
         ...rest,
      };
   });
   return {
      ...rest,
      items: formatedItems,
   };
}

export const trackGA4GrowthbookEvent = (trackData: GA4GrowthbookEvent) => {
   gtag('event', trackData.eventName, {
      'experiment_id': trackData.experimentId,
      'variation_id': trackData.variationId,
   });
};

export function trackCustomGAEvent({ eventAction, ...eventParams }: CustomAnalyticsEvent) {
   // The first arg is called "eventName" in GA4, but its conceptually closer to
   // the eventAction value in our tracking events. So here its sent as the first arg.
   // Also, "eventName" in ga4 has to be 40 chars or less
   const trimmedEventName = trimParam(eventAction, 40);
   // All params must be under 100 chars
   const trimmedParams = Object.keys(eventParams).reduce(
      (acc, key: string) => {
         if (key in eventParams) {
            const typedKey = key as keyof typeof eventParams;
            if (typeof eventParams[typedKey] === 'string') {
               acc[key] = trimParam(eventParams[typedKey] as string, 100);
            } else if (typeof eventParams[typedKey] === 'number') {
               acc[key] = eventParams[typedKey] as number;
            }
         }
         return acc;
      },
      {} as { [key: string]: string | number }
   );
   // Also send legacy GA4 parameter names for backwards compatibility
   // with existing reports that use event_category/event_label/event_value
   if (eventParams.eventCategory) {
      trimmedParams['event_category'] = trimParam(eventParams.eventCategory, 100);
   }
   if (eventParams.eventName) {
      trimmedParams['event_label'] = trimParam(eventParams.eventName, 100);
   }
   if (eventParams.eventValue != null) {
      trimmedParams['event_value'] = eventParams.eventValue;
   }
   gtag('event', trimmedEventName, trimmedParams);
}

const trimParam = (param: string, maxLength: number): string =>
   param.length > maxLength ? param.slice(0, maxLength - 3) + '...' : param;

export function trackGA4VisitedStore() {
   gtag('event', 'visited_store');
}

export function trackGA4PageBreadcrumbs(breadcrumbs: string) {
   gtag('event', 'page_breadcrumbs', { breadcrumbs: trimParam(breadcrumbs, 100) });
}

export function trackGA4FeaturedProducts(productSKUs: string[]) {
   gtag('event', 'featured_products', { product_skus: productSKUs.join(',').slice(0, 100) });
}

export function trackGA4CartUpdate(event: AnalyticsItemsEvent) {
   gtag('event', 'cart_update', formatEvent(event));
}

export function trackGA4PageView({
   page_location,
   page_title,
   page_referrer,
}: {
   page_location: string;
   page_title: string;
   page_referrer: string;
}) {
   gtag('event', 'page_view', {
      page_location,
      page_title,
      page_referrer,
   });
}
